import { AiPart } from "@intelligems/ig-types/schemas/ai/ui-message-part";
import { InputRequirement } from "@intelligems/ig-types/schemas/ai/nodes/input-requirement";
import { PickType } from "@intelligems/ig-types/utils";
import { ToolResultJSONType } from "@intelligems/ig-types/schemas/ai/nodes/tool-execution-result";
import {
  resolveSlot,
  useExecutionPanelSlot,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/ExecutionPanelContext";
import { DynamicTool } from "./DynamicTool";
import { DynamicTasks } from "./DynamicTasks";
import { AssistantText } from "./AssistantText";
import {
  FlattenedPart,
  MessageFlags,
} from "@src/features/ai/components/WorkflowEditor/execution/types";
import {
  isRequestUserInputTool,
  readRequestUserInputOutput,
} from "./requestUserInputOutput";
import {
  isCardAsk,
  isPromptTooLongForCardTitle,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/surveyAskState";

type ToolCallMessageProps = {
  item: FlattenedPart;
  part: PickType<AiPart, "dynamic-tool">;
} & MessageFlags;

export const DynamicToolOrTask = (props: ToolCallMessageProps) => {
  const {
    item,
    showToolsAsSteps,
    showToolCallOutput,
    hiddenTools,
    toolCallDefaultExpanded,
    disableResponseWordByWordLoading,
    part,
    debug,
    layoutDensity,
  } = props;
  const getSlot = useExecutionPanelSlot();
  const { toolName } = part;

  if (!part) {
    return null;
  }

  // A clarifying question is a visible reply to the merchant, not a step/card
  // — unless a card asks it. A card-backed ask (survey, combobox, stepped or
  // file card) is titled by the question itself, so the transcript does not
  // repeat it (ENG-7675); a free-text ask has no card, so its prompt is the
  // question and renders as assistant text. The one card-backed exception is a
  // prompt too long to title the card: the header then points "above", and the
  // full text has to be here for that to be true (see `surveyCardTitle`). Debug
  // mode keeps every prompt visible, marked, for inspecting what was asked.
  const inputRequirement =
    isRequestUserInputTool(toolName) && part.state === "output-available"
      ? readRequestUserInputOutput(part.output)
      : undefined;
  if (inputRequirement) {
    if (
      !debug &&
      isCardAsk(inputRequirement) &&
      !isPromptTooLongForCardTitle(inputRequirement.prompt ?? "")
    ) {
      return null;
    }
    return (
      <AssistantText
        item={item}
        part={{
          type: "text",
          text: `${debug ? "⚙️ " : ""}${inputRequirement.prompt}`,
          timestamp: Date.now(),
        }}
        debug={debug}
        disableResponseWordByWordLoading={disableResponseWordByWordLoading}
        layoutDensity={layoutDensity}
      />
    );
  }

  // A surface can draw a tool call itself. Checked before the display modes
  // below because it replaces them, and after `request-user-input` because
  // that is a merchant-facing reply rather than a tool call being reported.
  //
  // `undefined` means "not mine" and falls through, so a surface owning one
  // kind of call does not have to reimplement every other kind.
  const own = resolveSlot(getSlot("toolCard"), { item, part, flags: props });
  if (own !== undefined) {
    return <>{own}</>;
  }

  // Thought-process timeline: every tool call becomes a compact step (icon +
  // title + one-line description). Steps intentionally bypass `hiddenTools` —
  // the tools this chat hides as raw cards are exactly the actions we want to
  // surface as steps (created groups, set targeting, handed off to Viv).
  if (showToolsAsSteps) {
    return <DynamicTasks item={item} part={part} debug={debug} />;
  }

  if (hiddenTools?.includes(toolName)) {
    return null;
  }

  return (
    <DynamicTool
      part={part}
      debug={debug}
      item={item}
      showToolCallOutput={showToolCallOutput}
      toolCallDefaultExpanded={toolCallDefaultExpanded}
      disableResponseWordByWordLoading={disableResponseWordByWordLoading}
      layoutDensity={layoutDensity}
    />
  );
};
